import {
  Badge,
  Box,
  Flex,
  Heading,
  HStack,
  Icon,
  Text,
  useColorMode,
} from "@chakra-ui/react";
import Link from "next/link";
import { FiArrowRight, FiCalendar } from "react-icons/fi";

import type { NewsPostProps } from "./News";

export default function NewsCardPrimary({
  title,
  description,
  date_created,
  media,
  slug,
  category,
}: NewsPostProps) {
  const { colorMode } = useColorMode();
  const date = new Date(date_created);
  const fullURL = `${process.env.NEXT_PUBLIC_DIRECTUS_URL}/assets/${media}?width=800`;

  return (
    <Link href={`/posts/${slug}`} passHref>
      <Box
        bg={colorMode === "light" ? "white" : "neutral.800"}
        borderRadius="2xl"
        overflow="hidden"
        boxShadow={
          colorMode === "light"
            ? "0 20px 50px rgba(0, 0, 0, 0.1)"
            : "0 20px 50px rgba(0, 0, 0, 0.4)"
        }
        border="1px solid"
        borderColor={colorMode === "light" ? "neutral.100" : "neutral.800"}
        transition="transform 0.3s ease, box-shadow 0.3s ease"
        _hover={{
          transform: "translateY(-4px)",
          boxShadow:
            colorMode === "light"
              ? "0 30px 60px rgba(0, 0, 0, 0.15)"
              : "0 30px 60px rgba(0, 0, 0, 0.5)",
        }}
        cursor="pointer"
        position="relative"
      >
        {/* Gradient accent line */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          h="3px"
          bgGradient="linear(to-r, brand.500, brand.700)"
          zIndex={1}
        />

        <Flex direction={{ base: "column", lg: "row" }}>
          {/* Image */}
          <Box
            w={{ base: "100%", lg: "55%" }}
            h={{ base: "250px", md: "300px", lg: "auto" }}
            minH={{ lg: "350px" }}
            position="relative"
            overflow="hidden"
          >
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              bgSize="cover"
              bgPosition="center"
              style={{
                backgroundImage: `url(${fullURL})`,
              }}
              transition="transform 0.5s ease"
              _groupHover={{ transform: "scale(1.05)" }}
            />
            {/* Image overlay gradient */}
            <Box
              position="absolute"
              bottom={0}
              left={0}
              right={0}
              h="50%"
              bgGradient="linear(to-t, rgba(0,0,0,0.4), transparent)"
              display={{ base: "block", lg: "none" }}
            />
          </Box>

          {/* Content */}
          <Flex
            direction="column"
            p={{ base: 6, md: 8 }}
            w={{ base: "100%", lg: "45%" }}
            justify="center"
          >
            <Box mb={4}>
              <Badge
                bg="brand.500"
                color="white"
                px={3}
                py={1}
                borderRadius="full"
                fontSize="xs"
                fontWeight={700}
                textTransform="uppercase"
                letterSpacing="0.05em"
              >
                {category}
              </Badge>
            </Box>

            <Heading
              fontSize={{ base: "xl", md: "2xl", lg: "3xl" }}
              fontWeight={700}
              color={
                colorMode === "light"
                  ? "text.light-primary"
                  : "text.dark-primary"
              }
              lineHeight="1.3"
              mb={4}
            >
              {title}
            </Heading>

            <Text
              color={
                colorMode === "light"
                  ? "text.light-secondary"
                  : "text.dark-muted"
              }
              fontSize={{ base: "sm", md: "md" }}
              lineHeight="1.8"
              mb={6}
              noOfLines={3}
            >
              {description}
            </Text>

            <Flex justify="space-between" align="center" mt="auto">
              <HStack
                spacing={2}
                color={
                  colorMode === "light" ? "text.light-muted" : "text.dark-muted"
                }
              >
                <Icon as={FiCalendar} boxSize={4} aria-hidden="true" />
                <Text fontSize="sm" fontWeight={500}>
                  {new Intl.DateTimeFormat("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  }).format(date)}
                </Text>
              </HStack>

              <HStack
                spacing={2}
                color="brand.500"
                fontWeight={600}
                fontSize="sm"
                transition="transform 0.2s ease"
                _hover={{ transform: "translateX(4px)" }}
              >
                <Text>Read More</Text>
                <Icon as={FiArrowRight} aria-hidden="true" />
              </HStack>
            </Flex>
          </Flex>
        </Flex>
      </Box>
    </Link>
  );
}
